import React from "react";
import {
  Wrench,
  Hammer,
  Plug,
  ShieldCheck,
  Ruler,
  Paintbrush,
  Drill,
  Flame,
  Lightbulb,
  Fan,
  Box,
  Lock,
  Layers,
  Package,
  Lamp,
  LucideGlassWater,
  Bath,
} from "lucide-react";

/* ================= TYPES ================= */
export interface CategoryItem {
  id: number;
  name: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  parentId?: number | null;
  color?: string;
     image?: any;
}

/* ──────────────────────────────────────────────
   1️⃣ TOP-LEVEL TOOLS CATEGORIES (1000000xx)
   ────────────────────────────────────────────── */
export const toolsCategories: CategoryItem[] = [
  { id: 100000001, name: "Tools & Home Improvement", icon: Wrench, color: "#F57C00" },
  { id: 100000002, name: "Appliances", icon: Fan, color: "#4CAF50" },
  { id: 100000003, name: "Building Supplies", icon: Layers, color: "#6D4C41" },
  { id: 100000004, name: "Electrical", icon: Plug, color: "#1976D2" },
  { id: 100000005, name: "Hardware", icon: Hammer, color: "#455A64" },
  { id: 100000006, name: "Kitchen & Bath Fixtures", icon: Bath, color: "#00897B" },
  { id: 100000007, name: "Light Bulbs", icon: Lightbulb, color: "#FBC02D" },
  { id: 100000008, name: "Lighting & Ceiling Fans", icon: Fan, color: "#FFA000" },
  { id: 100000009, name: "Measuring & Layout Tools", icon: Ruler, color: "#5D4037" },
  { id: 100000010, name: "Painting Supplies & Wall Treatments", icon: Paintbrush, color: "#8E24AA" },
  { id: 100000011, name: "Power & Hand Tools", icon: Drill, color: "#D32F2F" },
  { id: 100000012, name: "Rough Plumbing", icon: Flame, color: "#0288D1" },
  { id: 100000013, name: "Safety & Security", icon: ShieldCheck, color: "#2E7D32" },
  { id: 100000014, name: "Storage & Home Organization", icon: Box, color: "#546E7A" },
  { id: 100000015, name: "Welding & Soldering", icon: Lamp, color: "#BF360C" },
];

/* ──────────────────────────────────────────────
   2️⃣ SUBCATEGORIES (1000001xx)
   ────────────────────────────────────────────── */
export const toolsSubcategories: CategoryItem[] = [
  { id: 100000101, parentId: 100000001, name: "General Tools", icon: Wrench },
  { id: 100000102, parentId: 100000001, name: "Home Repair", icon: Hammer },

  { id: 100000201, parentId: 100000002, name: "Large Appliances", icon: Fan },
  { id: 100000202, parentId: 100000002, name: "Small Appliances", icon: Fan },

  { id: 100000301, parentId: 100000003, name: "Lumber & Timber", icon: Layers },
  { id: 100000302, parentId: 100000003, name: "Cement & Concrete", icon: Layers },

  { id: 100000401, parentId: 100000004, name: "Wires & Cables", icon: Plug },
  { id: 100000402, parentId: 100000004, name: "Switches & Sockets", icon: Plug },

  { id: 100000501, parentId: 100000005, name: "Fasteners", icon: Hammer },
  { id: 100000502, parentId: 100000005, name: "Hinges & Brackets", icon: Hammer },

  { id: 100000601, parentId: 100000006, name: "Faucets & Taps", icon: LucideGlassWater },
  { id: 100000602, parentId: 100000006, name: "Sinks & Showers", icon: Bath },

  { id: 100000701, parentId: 100000007, name: "LED Bulbs", icon: Lightbulb },
  { id: 100000702, parentId: 100000007, name: "Smart Bulbs", icon: Lightbulb },

  { id: 100000801, parentId: 100000008, name: "Ceiling Lights", icon: Lightbulb },
  { id: 100000802, parentId: 100000008, name: "Ceiling Fans", icon: Fan },

  { id: 100000901, parentId: 100000009, name: "Measuring Tapes", icon: Ruler },
  { id: 100000902, parentId: 100000009, name: "Levels & Squares", icon: Ruler },

  { id: 100001001, parentId: 100000010, name: "Paints", icon: Paintbrush },
  { id: 100001002, parentId: 100000010, name: "Rollers & Brushes", icon: Paintbrush },

  { id: 100001101, parentId: 100000011, name: "Power Tools", icon: Drill },
  { id: 100001102, parentId: 100000011, name: "Hand Tools", icon: Wrench },

  { id: 100001201, parentId: 100000012, name: "Pipes & Fittings", icon: Flame },
  { id: 100001202, parentId: 100000012, name: "Valves & Pumps", icon: Flame },

  { id: 100001301, parentId: 100000013, name: "Locks & Safes", icon: Lock },
  { id: 100001302, parentId: 100000013, name: "Protective Gear", icon: ShieldCheck },

  { id: 100001401, parentId: 100000014, name: "Shelving", icon: Box },
  { id: 100001402, parentId: 100000014, name: "Tool Storage", icon: Package },

  { id: 100001501, parentId: 100000015, name: "Welding Machines", icon: Lamp },
  { id: 100001502, parentId: 100000015, name: "Soldering Irons", icon: Lamp },
];

/* ──────────────────────────────────────────────
   3️⃣ PRODUCTS (1000101xx+)
   ────────────────────────────────────────────── */
export const toolsProducts: CategoryItem[] = [
  { id: 100010101, parentId: 100000101, name: "Multi-Purpose Tool Kit", icon: Wrench },
  { id: 100010102, parentId: 100000101, name: "Adjustable Spanner Set", icon: Wrench },

  { id: 100020101, parentId: 100000201, name: "Washing Machine", icon: Fan },
  { id: 100020102, parentId: 100000201, name: "Refrigerator", icon: Fan },

  { id: 100040101, parentId: 100000401, name: "Copper Electrical Wire Roll", icon: Plug },

  { id: 100060101, parentId: 100000601, name: "Stainless Steel Faucet", icon: LucideGlassWater },

  { id: 100070101, parentId: 100000701, name: "LED Bulb Pack", icon: Lightbulb },

  { id: 100110101, parentId: 100001101, name: "Cordless Power Drill", icon: Drill },

  { id: 100130101, parentId: 100001301, name: "Digital Door Safe", icon: Lock },

  { id: 100150101, parentId: 100001501, name: "Arc Welding Machine", icon: Lamp },
];
